import { Component, ElementRef, NgZone } from '@angular/core';
import { RGB } from '../hero-motif/hero-motif.base';
import { Branch, HeroSakuraComponent } from '../hero-sakura/hero-sakura.component';

/**
 * Fuji's hero board (2026-10-16), after the theme's own photograph (cover.webp): Mount Fuji alone in
 * the middle, big and far off, under a pink "vanilla sky", gold low at the right; its snow cap reaching
 * down its gullies, a band of cloud drifting at its foot, the Chureito pagoda at the right. In front at
 * the lower left, Sakura's own cherry branch in Fuji's peach, swaying and shedding its petals (Dock Town's
 * Fuji: falling petals); a gust (the pointer, a finger, the counter counting) sheds far more. The
 * page's wallpaper is the petals alone. Built on HeroSakuraComponent: its tree and petals, Fuji's
 * scene painted as its ground.
 */
@Component( {
  selector: 'app-hero-fuji',
  template: '<canvas #canvas class="hfj-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hfj-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroFujiComponent extends HeroSakuraComponent {

  private back: HTMLCanvasElement | null = null;
  private front: HTMLCanvasElement | null = null;
  private mist: HTMLCanvasElement | null = null;
  private UW = 0;
  private trunk = { x: 0, y: 0, w: 0 };

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    // fewer and smaller blossoms than Sakura's own (owner)
    this.flowerCount = 2.8;
    this.flowerScale = 0.52;
  }

  /** The canvas's device scale, for blur radii. */
  private dpr( g: CanvasRenderingContext2D ): number {
    return g.getTransform().a;
  }

  protected colorsChanged(): void {
    super.colorsChanged();
    this.back = null;
    this.front = null;
    this.mist = null;
  }

  protected build( rand: () => number ): void {
    super.build( rand );
    this.UW = Math.min( this.width, this.height * 2.1 );
    this.back = null;
    this.front = null;
    this.mist = null;
  }

  /**
   * The branch at the lower left, a little toward the middle (owner): Sakura's fan of limbs from a
   * root just under the stage's foot, turned up, so what shows is a half disc of blossom rising there.
   */
  protected branches( rand: () => number, tall: boolean ): Branch[] {
    const k = this.scaleK;
    const W = this.width;
    const H = this.height;
    const UW = Math.min( W, H * 2.1 );
    const radius = ( tall ? Math.min( W * 0.4, H * 0.135 ) : Math.min( UW * 0.2, H * 0.36 ) ) * 1.05;
    // a little up from the foot, so the trunk the limbs part from shows (owner)
    const root = { x: W / 2 - UW * 0.24 - 30, y: H - radius * 0.24 };
    const fan = tall ? 5 : 6;
    // the trunk, drawn by paintGround: straight up from below the stage's foot to the hub, one width
    // all the way (owner)
    this.trunk = { x: root.x, y: root.y, w: 30 * k };
    const branches: Branch[] = [];
    for ( let f = 0; f < fan; f++ ) {
      const angle = -Math.PI + 0.12 + ( Math.PI - 0.24 ) * ( f + 0.5 ) / fan + ( rand() - 0.5 ) * 0.14;
      branches.push( { ...root, angle, len: radius * 0.55, width: ( 15 + rand() * 6 ) * k, depth: 3, droop: 0, radius: radius * 1.12, top: H * 0.03 } );
    }
    return branches;
  }

  /** The limbs carry Sakura's blossom, more at their tips. */
  protected dress( b: Branch, rand: () => number ): void {
    const from = this.sprays.length;
    super.dress( b, rand );
    // more blossom at the tips (owner): the sprays at the limbs' ends and on the twigs get as many again
    for ( let i = from; i < this.sprays.length; i++ ) {
      const sp = this.sprays[ i ];
      if ( sp.at < 1 && !this.limbs[ sp.limb ].twig ) { continue; }
      const extra = sp.flowers.map( fl => ( { ...fl, dx: fl.dx * 1.3 + ( rand() - 0.5 ) * 14 * this.scaleK, dy: fl.dy * 1.3 + ( rand() - 0.5 ) * 12 * this.scaleK, tint: Math.floor( rand() * 3 ) } ) );
      sp.flowers.push( ...extra );
    }
  }

  /** The cherry wears no leaves here: blossom only, in Fuji's peach. */
  protected makeSprites(): void {
    super.makeSprites();
    this.leafSprite = null;
  }

  /** Fuji's scene, under the tree: the sky and the mountain, the drifting cloud band, the pagoda. */
  protected paintGround( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.front || !this.mist ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    // the cloud band drifting sideways (owner), 16 px a second like Everest's
    const off = Math.round( ( performance.now() / 1000 * 16 * this.scaleK ) % W );
    ctx.drawImage( this.mist, off, 0, W, H );
    ctx.drawImage( this.mist, off - W, 0, W, H );
    ctx.drawImage( this.front, 0, 0, W, H );
    this.paintTrunk( ctx );
  }

  /** The cherry's trunk: one even width from the stage's foot up to the hub, lit on its left, banded. */
  private paintTrunk( ctx: CanvasRenderingContext2D ): void {
    const { x, y, w } = this.trunk;
    if ( !w ) { return; }
    const H = this.height;
    const bark = this.barkColor( this.five()[ 1 ] );
    const shine = this.mix( bark, [ 255, 255, 255 ], 0.38 );
    const tg = ctx.createLinearGradient( x - w / 2, 0, x + w / 2, 0 );
    tg.addColorStop( 0, this.rgba( this.mix( bark, shine, 0.25 ), 1 ) );
    tg.addColorStop( 0.35, this.rgba( this.mix( bark, shine, 0.12 ), 1 ) );
    tg.addColorStop( 1, this.rgba( this.mix( bark, [ 20, 6, 10 ], 0.25 ), 1 ) );
    ctx.fillStyle = tg;
    ctx.fillRect( x - w / 2, y, w, H - y + 2 );
    // the cherry's bark marks: short light bands across it
    ctx.strokeStyle = this.rgba( this.mix( bark, shine, 0.3 ), 1 );
    ctx.lineWidth = 1.2;
    for ( let m = y + 14; m < H; m += 15 ) {
      ctx.beginPath();
      ctx.moveTo( x - w * 0.3, m );
      ctx.lineTo( x + w * 0.15, m + 1 );
      ctx.stroke();
    }
  }

  /**
   * The still parts, painted once: the sky and the mountain on the back layer; the cloud band at its
   * foot on a drifting layer; the pagoda on the front one.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = this.UW;
    const [ peach, coral, mauve, slate ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g = c.getContext( '2d' )!;
      g.scale( scale, scale );
      return { c, g };
    };
    const { c, g } = canvas();
    const horizon = H * 0.66;
    // the sky: a "vanilla sky" (owner's photo), its pink without the wisps (owner: the cloud band at
    // the mountain's foot is the cloud): a soft blue pink high up, rose in the middle, gold where the low
    // sun glows at the lower right
    const rose = this.mix( coral, white, 0.5 );
    const blueTop: RGB = this.mix( this.mix( [ 112, 160, 226 ], slate, 0.15 ), rose, 0.6 );
    const sky = g.createLinearGradient( 0, 0, 0, horizon );
    sky.addColorStop( 0, this.rgba( blueTop, 1 ) );
    sky.addColorStop( 0.5, this.rgba( this.mix( rose, white, 0.15 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( this.mix( rose, peach, 0.6 ), white, 0.25 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, H );
    const R = Math.max( W, H );
    const sunX = W / 2 + UW * 0.45, sunY = H * 0.62;
    const glow = g.createRadialGradient( sunX, sunY, 0, sunX, sunY, R * 0.5 );
    glow.addColorStop( 0, this.rgba( [ 255, 226, 170 ], 0.6 ) );
    glow.addColorStop( 0.5, this.rgba( this.mix( peach, [ 255, 200, 160 ], 0.4 ), 0.2 ) );
    glow.addColorStop( 1, this.rgba( peach, 0 ) );
    g.fillStyle = glow;
    g.fillRect( 0, 0, W, H );

    // the mountain: Fuji alone in the middle, its concave slopes from the narrow flat summit, wide at its foot
    const cx = W / 2;
    const peakY = H * 0.18;
    const footY = horizon;
    // spread wide like the real mountain, its slopes long and gentle (owner)
    const span = UW * 1.25;
    const flank = ( side: -1 | 1, f: number ) => {
      const x = cx + side * ( UW * 0.034 + f * span * 0.5 );
      const y = peakY + ( footY - peakY ) * ( 1 - Math.pow( 1 - f, 2.6 ) );
      return [ x, y ];
    };
    const outline = ( gg: CanvasRenderingContext2D ) => {
      gg.beginPath();
      const [ x0, y0 ] = flank( -1, 1 );
      gg.moveTo( x0, y0 + 2 );
      for ( let i = 40; i >= 0; i-- ) { const [ x, y ] = flank( -1, i / 40 ); gg.lineTo( x, y ); }
      // the summit, a little uneven
      gg.lineTo( cx - UW * 0.013, peakY - H * 0.004 );
      gg.lineTo( cx + UW * 0.004, peakY + H * 0.002 );
      gg.lineTo( cx + UW * 0.017, peakY - H * 0.003 );
      for ( let i = 0; i <= 40; i++ ) { const [ x, y ] = flank( 1, i / 40 ); gg.lineTo( x, y ); }
      gg.closePath();
    };
    const rock = this.mix( this.mix( slate, mauve, 0.25 ), white, 0.2 );
    outline( g );
    const mg = g.createLinearGradient( cx - span / 2, 0, cx + span / 2, 0 );
    // lit peach on the side toward the dawn, blue in the shade
    mg.addColorStop( 0, this.rgba( this.mix( rock, peach, 0.4 ), 1 ) );
    mg.addColorStop( 0.5, this.rgba( rock, 1 ) );
    mg.addColorStop( 1, this.rgba( this.mix( rock, slate, 0.4 ), 1 ) );
    g.fillStyle = mg;
    g.fill();
    // the snow cap, reaching down the gullies in fingers, lit peach on the left
    g.save();
    outline( g );
    g.clip();
    // a soft streak along the slope from (x, y): the gullies and ridges radiate from above the summit
    const apexX = cx, apexY = peakY - H * 0.06;
    const streak = ( x: number, y: number, len: number, wid: number, colour: RGB, a: number ) => {
      g.save();
      g.translate( x, y );
      g.rotate( Math.atan2( y - apexY, x - apexX ) );
      g.scale( len, wid );
      const sk = g.createRadialGradient( 0.35, 0, 0, 0.35, 0, 1 );
      sk.addColorStop( 0, this.rgba( colour, a ) );
      sk.addColorStop( 1, this.rgba( colour, 0 ) );
      g.fillStyle = sk;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    };
    // the snowline: the cap's edge, fingers of snow reaching down the gullies, each its own length
    // (fixed seed, the same on every screen), long and narrow near the middle, short toward the sides
    const fr0 = this.seededLocal( 17 );
    const fingers: [ number, number, number ][] = [];
    for ( let i = 0; i < 34; i++ ) {
      const d = -0.62 + 1.24 * ( i + 0.5 ) / 34 + ( fr0() - 0.5 ) * 0.02;
      fingers.push( [ cx + d * span * 0.5, span * ( 0.008 + fr0() * 0.008 ), ( 0.25 + fr0() * 0.75 ) * ( 1 - Math.abs( d ) * 0.7 ) ] );
    }
    const snowLine = ( x: number ) => {
      const d = ( x - cx ) / ( span * 0.5 );
      let y = peakY + H * ( 0.085 + 0.035 * Math.abs( d ) );
      for ( const [ fx, fw, fl ] of fingers ) {
        const t = ( x - fx ) / fw;
        if ( t > -1 && t < 1 ) { y += Math.pow( 1 - t * t, 1.6 ) * fl * H * 0.085; }
      }
      return y;
    };
    const sg = g.createLinearGradient( cx - span * 0.25, 0, cx + span * 0.25, 0 );
    sg.addColorStop( 0, this.rgba( this.mix( white, peach, 0.35 ), 1 ) );
    sg.addColorStop( 0.55, this.rgba( white, 1 ) );
    sg.addColorStop( 1, this.rgba( this.mix( white, slate, 0.22 ), 1 ) );
    // the rock's own gullies first, soft darker runs below the fingers, fading down the slope
    const ravine = this.mix( rock, slate, 0.35 );
    for ( const [ fx, fw, fl ] of fingers ) {
      const y = snowLine( fx );
      streak( fx, y + H * 0.04, H * ( 0.06 + fl * 0.08 ), fw * 0.7, ravine, 0.18 );
    }
    // the snow, its edge soft (Everest's soft snowline)
    g.save();
    g.shadowColor = this.rgba( this.mix( white, rock, 0.35 ), 0.8 );
    g.shadowBlur = 6 * k * this.dpr( g );
    g.fillStyle = sg;
    g.beginPath();
    g.moveTo( cx - span * 0.34, peakY - 4 );
    for ( let x = cx - span * 0.34; x <= cx + span * 0.34; x += 1.5 ) { g.lineTo( x, snowLine( x ) ); }
    g.lineTo( cx + span * 0.34, peakY - 4 );
    g.closePath();
    g.fill();
    g.restore();
    // the relief in the snow: each finger's shaded side a pale blue run, its lit side a warm one,
    // fading out toward the summit
    for ( const [ fx, fw, fl ] of fingers ) {
      const y = snowLine( fx );
      const len = y - peakY;
      const shadeSide = fx > cx ? 1 : 0.6;
      streak( fx + fw * 0.55, y - len * 0.35, len * 0.45, fw * 0.32, this.mix( white, slate, 0.45 ), 0.3 * shadeSide );
      streak( fx - fw * 0.5, y - len * 0.35, len * 0.4, fw * 0.25, this.mix( white, peach, 0.6 ), 0.25 * ( 1.6 - shadeSide ) );
    }
    // the faces: the left lit warm by the dawn, the right in a soft blue shade (soft washes, no lamp)
    for ( const [ side, colour, a ] of [ [ -1, this.mix( peach, coral, 0.2 ), 0.16 ], [ 1, this.mix( slate, mauve, 0.3 ), 0.2 ] ] as [ number, RGB, number ][] ) {
      g.save();
      g.translate( cx + side * span * 0.2, peakY + ( footY - peakY ) * 0.55 );
      g.scale( span * 0.24, ( footY - peakY ) * 0.5 );
      const wg = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      wg.addColorStop( 0, this.rgba( colour, a ) );
      wg.addColorStop( 1, this.rgba( colour, 0 ) );
      g.fillStyle = wg;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    // a haze rising from the foot
    const hz = g.createLinearGradient( 0, peakY + H * 0.15, 0, footY );
    hz.addColorStop( 0, this.rgba( this.mix( peach, white, 0.4 ), 0 ) );
    hz.addColorStop( 1, this.rgba( this.mix( peach, white, 0.4 ), 0.6 ) );
    g.fillStyle = hz;
    g.fillRect( 0, peakY, W, footY - peakY );
    g.restore();
    // the land below, flat and far off in the haze
    const land = g.createLinearGradient( 0, horizon, 0, H );
    land.addColorStop( 0, this.rgba( this.mix( this.mix( slate, mauve, 0.3 ), white, 0.55 ), 1 ) );
    land.addColorStop( 1, this.rgba( this.mix( this.mix( slate, mauve, 0.3 ), white, 0.4 ), 1 ) );
    g.fillStyle = land;
    g.fillRect( 0, horizon, W, H - horizon );
    // a clearing of light behind the hero's words
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( white, 0.3 ) );
      cl.addColorStop( 1, this.rgba( white, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.back = c;

    // the cloud band at the mountain's foot
    const mi = canvas();
    const mr = this.seededLocal( 11 );
    // a thick bank (owner: more cloud), heaped a little up the mountain's foot
    for ( let i = 0; i < 90; i++ ) {
      const x = mr() * W;
      const y = horizon - H * ( 0.06 - mr() * 0.1 );
      const rx = ( 80 + mr() * 160 ) * k, ry = ( 16 + mr() * 22 ) * k;
      for ( const dx of [ -W, 0, W ] ) {
        mi.g.save();
        mi.g.translate( x + dx, y );
        mi.g.scale( rx, ry );
        const pg = mi.g.createRadialGradient( 0, -0.25, 0, 0, 0, 1 );
        pg.addColorStop( 0, this.rgba( this.mix( white, peach, 0.2 ), 0.85 ) );
        pg.addColorStop( 1, this.rgba( white, 0 ) );
        mi.g.fillStyle = pg;
        mi.g.fillRect( -1, -1, 2, 2 );
        mi.g.restore();
      }
    }
    this.mist = mi.c;

    // the front: the pagoda across from the cherry, mirrored on the other side, its foot cut off by
    // the stage's edge (owner)
    const fr = canvas();
    this.pagoda( fr.g, W / 2 + UW * 0.24 + 90, H * 1.2, H * 0.96, horizon );
    this.front = fr.c;
  }

  /**
   * The Chureito pagoda as photographs of it with Fuji show it (owner's photo), in perspective: seen
   * three quarters on, its left face narrow and its front face wide, at eye level with the horizon, so
   * the roofs above the eye show their red raftered undersides and the ones below show their grey green
   * tiles. Five storeys, each a cream walled body framed in vermilion posts and beams with a red rail
   * round its balcony, under a broad hipped roof whose eaves sweep up at the corners and whose hip ridges
   * run in from them; the dark bronze spire with its rings on top. `x` its centre, `base` its foot (under
   * the stage's edge, so it is cut off there), `h` its height, `eye` the eye level.
   */
  private pagoda( f: CanvasRenderingContext2D, x: number, base: number, h: number, eye: number ): void {
    const [ , coral, , slate ] = this.five();
    const red: RGB = this.mix( [ 206, 46, 34 ], coral, 0.1 );
    const redShade = this.mix( red, [ 70, 12, 10 ], 0.3 );
    const redDark = this.mix( red, [ 70, 12, 10 ], 0.5 );
    const orange: RGB = [ 236, 112, 58 ];
    const cream: RGB = [ 242, 226, 204 ];
    const creamShade = this.mix( cream, [ 150, 120, 120 ], 0.3 );
    const tile = this.mix( this.mix( slate, [ 92, 104, 94 ], 0.55 ), [ 255, 255, 255 ], 0.1 );
    const tileShade = this.mix( tile, [ 30, 36, 34 ], 0.3 );
    const tileDark = this.mix( tile, [ 30, 36, 34 ], 0.5 );
    const line = Math.max( 0.8, h * 0.0025 );
    // the view: turned a little, 17 degrees, so the left face shows narrow (owner: slightly from the side), and depth pulling every level toward the eye level a little
    const th = 0.3;
    const cs = Math.cos( th ), sn = Math.sin( th );
    const P = ( X: number, Z: number, y: number ): [ number, number ] => {
      const d = ( X * sn + Z * cs ) / h;
      return [ x + ( X * cs - Z * sn ) * ( 1 - d * 0.35 ), eye + ( y - eye ) * ( 1 - d * 0.35 ) ];
    };
    // the two faces we see: the left (X = -s) from the back corner to the near one, the front (Z = -s)
    // from the near corner to the right one; u runs 0..1 along a face
    const onFace = ( face: 0 | 1, s: number, u: number, y: number ) =>
      face === 0 ? P( -s, s - 2 * s * u, y ) : P( -s + 2 * s * u, -s, y );
    const quad = ( face: 0 | 1, s: number, u0: number, u1: number, y0: number, y1: number, colour: RGB ) => {
      f.fillStyle = this.rgba( colour, 1 );
      f.beginPath();
      f.moveTo( ...onFace( face, s, u0, y0 ) );
      f.lineTo( ...onFace( face, s, u1, y0 ) );
      f.lineTo( ...onFace( face, s, u1, y1 ) );
      f.lineTo( ...onFace( face, s, u0, y1 ) );
      f.closePath();
      f.fill();
    };
    const storeys = 5;
    const spire = h * 0.2;
    const step = ( h - spire ) / storeys;
    const sorin = () => {
      // the sorin as photographs show it, in green weathered bronze: a low stepped square base on the top
      // roof's flat top, a lotus cup, nine thick rings stacked close, the openwork flame cage and the thin
      // finial with its knobs
      const sbTop = h * 0.17 * ( 1 - ( storeys - 1 ) * 0.05 ) * 0.6;
      const ridgeY = base - storeys * step;
      const [ sx ] = P( 0, 0, ridgeY );
      // seen from below the eye, the top roof hides the base's foot (owner): the sorin is drawn first and
      // stands just down into the roof's peak, which is drawn over it
      const [ , mid ] = P( 0, 0, ridgeY );
      const top = mid + step * 0.03;
      const verd: RGB = this.mix( [ 74, 98, 86 ], slate, 0.15 );
      const verdLit = this.mix( verd, [ 220, 235, 225 ], 0.35 );
      const verdDark = this.mix( verd, [ 15, 25, 22 ], 0.5 );
      const wB = sbTop * 0.32;
      const bar = ( y0: number, y1: number, hw: number ) => {
        const g2 = f.createLinearGradient( sx - hw, 0, sx + hw, 0 );
        g2.addColorStop( 0, this.rgba( verdLit, 1 ) );
        g2.addColorStop( 0.45, this.rgba( verd, 1 ) );
        g2.addColorStop( 1, this.rgba( verdDark, 1 ) );
        f.fillStyle = g2;
        f.fillRect( sx - hw, y1, hw * 2, y0 - y1 );
      };
      // the stepped base: square boxes turned with the pagoda, their left faces in shade (the round parts
      // above need no turning)
      const box = ( half: number, y0: number, y1: number ) => {
        for ( const face of [ 0, 1 ] as ( 0 | 1 )[] ) {
          const pts = [ onFace( face, half, 0, y0 ), onFace( face, half, 1, y0 ), onFace( face, half, 1, y1 ), onFace( face, half, 0, y1 ) ];
          f.fillStyle = this.rgba( face === 0 ? verdDark : this.mix( verd, verdLit, 0.3 ), 1 );
          f.beginPath();
          for ( const [ px, py ] of pts ) { f.lineTo( px + sx - x, py ); }
          f.closePath();
          f.fill();
        }
      };
      // the lower box as wide as the roof's peak, its corners on the hip lines (owner)
      box( sbTop * 0.31, top, top - spire * 0.035 );
      box( sbTop * 0.24, top - spire * 0.035, top - spire * 0.06 );
      // the lotus cup, flaring up in petals
      f.fillStyle = this.rgba( verd, 1 );
      f.beginPath();
      f.moveTo( sx - wB * 0.4, top - spire * 0.06 );
      const petals = 5;
      for ( let q = 0; q <= petals; q++ ) {
        const u = q / petals * 2 - 1;
        f.lineTo( sx + u * wB * 0.85, top - spire * ( q % 2 ? 0.115 : 0.13 ) );
      }
      f.lineTo( sx + wB * 0.4, top - spire * 0.06 );
      f.closePath();
      f.fill();
      // the mast behind the rings
      f.fillStyle = this.rgba( verdDark, 1 );
      f.fillRect( sx - wB * 0.12, top - spire * 0.95, wB * 0.24, spire * 0.85 );
      // the nine rings, thick and rounded, stacked close, all nearly one width
      for ( let q = 0; q < 9; q++ ) {
        const cy = top - spire * ( 0.16 + q * 0.054 );
        const rw = wB * ( 0.72 - q * 0.012 );
        const rh = spire * 0.022;
        const rg2 = f.createLinearGradient( 0, cy - rh, 0, cy + rh );
        rg2.addColorStop( 0, this.rgba( verdLit, 1 ) );
        rg2.addColorStop( 0.5, this.rgba( verd, 1 ) );
        rg2.addColorStop( 1, this.rgba( verdDark, 1 ) );
        f.fillStyle = rg2;
        f.beginPath();
        f.ellipse( sx, cy, rw, rh, 0, 0, Math.PI * 2 );
        f.fill();
      }
      // the openwork flame cage: a light frame with its lattice
      const cy0 = top - spire * 0.66, cy1 = top - spire * 0.82;
      f.strokeStyle = this.rgba( verdDark, 1 );
      f.lineWidth = Math.max( 0.8, wB * 0.05 );
      f.beginPath();
      f.moveTo( sx - wB * 0.3, cy0 );
      f.quadraticCurveTo( sx - wB * 0.42, ( cy0 + cy1 ) / 2, sx - wB * 0.22, cy1 );
      f.lineTo( sx + wB * 0.22, cy1 );
      f.quadraticCurveTo( sx + wB * 0.42, ( cy0 + cy1 ) / 2, sx + wB * 0.3, cy0 );
      f.closePath();
      f.stroke();
      f.lineWidth = Math.max( 0.5, wB * 0.025 );
      for ( let q = 1; q < 4; q++ ) {
        const yy = cy0 + ( cy1 - cy0 ) * q / 4;
        f.beginPath();
        f.moveTo( sx - wB * 0.34, yy );
        f.lineTo( sx + wB * 0.34, yy );
        f.stroke();
      }
      // the finial: a thin rod with its small knobs
      f.fillStyle = this.rgba( verd, 1 );
      f.fillRect( sx - wB * 0.06, top - spire, wB * 0.12, spire * 0.2 );
      for ( const k2 of [ 0.86, 0.92, 0.98 ] ) {
        f.beginPath();
        f.ellipse( sx, top - spire * k2, wB * 0.13, spire * 0.012, 0, 0, Math.PI * 2 );
        f.fill();
      }
    };
    sorin();
    // storey by storey from the bottom up, each its walls then its roof, so every storey stands drawn
    // over the roof below it (owner)
    for ( let i = 0; i < storeys; i++ ) for ( const pass of [ 0, 1 ] ) {
      const yb = base - i * step;
      const sr = h * 0.17 * ( 1 - i * 0.05 );
      const sb = sr * 0.6;
      const bh = step * ( i === 0 ? 0.52 : 0.44 );
      const ry = yb - bh;
      if ( pass === 0 ) {
      const foot = yb;
      // the body: cream walls in bays framed by vermilion posts and beams, the left face in shade
      for ( const face of [ 0, 1 ] as ( 0 | 1 )[] ) {
        const shade = face === 0;
        quad( face, sb, 0, 1, foot, ry, shade ? creamShade : cream );
        const post = shade ? redShade : red;
        const pw = line * 3 / ( 2 * sb );
        // the corner posts stay inside the wall's edge, so the corners meet the roof above exactly (owner)
        for ( let p = 0; p <= 3; p++ ) { quad( face, sb, Math.max( 0, p / 3 - pw ), Math.min( 1, p / 3 + pw ), yb, ry, post ); }
        quad( face, sb, 0, 1, ry + bh * 0.1, ry, post );
        quad( face, sb, 0, 1, ry + bh * 0.42 + line * 2.4, ry + bh * 0.42, post );
        quad( face, sb, 1 / 3 + pw * 2, 2 / 3 - pw * 2, yb - line, ry + bh * 0.48, this.mix( shade ? creamShade : cream, red, 0.2 ) );
      }
      // the balcony round its foot, a red rail on posts, standing on the roof below
      if ( i > 0 ) {
        const sa = sb * 1.02;
        const rh = bh * 0.26;
        for ( const face of [ 0, 1 ] as ( 0 | 1 )[] ) {
          const c = face === 0 ? redShade : red;
          quad( face, sa, 0, 1, yb - rh + line * 2.2, yb - rh, c );
          quad( face, sa, 0, 1, yb, yb - line * 2, c );
          const n = face === 0 ? 8 : 12;
          for ( let q = 0; q <= n; q++ ) { quad( face, sa, q / n - 0.006, q / n + 0.006, yb, yb - rh, c ); }
        }
      }
      continue;
      }
      // the roof: its eave sweeping up to the corners, sloping in to the storey above
      // every roof shows its raftered underside under the eave, as the top one does (owner)
      const below = 1;
      const eave = ry - step * ( 0.04 + 0.12 * below );
      // the corners swept up into sharp points, as in the photograph (owner)
      const up = step * 0.24;
      // the roof rises right to the foot of the storey above, so nothing floats
      const roofTop = yb - step;
      // each roof's top is exactly as wide as the storey above, meeting its walls at their foot (owner:
      // every storey sits right on its roof); the top one is a hipped pyramid as on the real pagoda, its four
      // slopes and hips gathering up to the sorin's small base
      const sTop = i < storeys - 1 ? h * 0.17 * ( 1 - ( i + 1 ) * 0.05 ) * 0.6 : sb * 0.3;
      // the near corner (where the two faces meet) lifts less, so the narrow left face does not curl into
      // a U (owner); the sharp points stay at the outer corners
      const eaveAt = ( face: 0 | 1, u: number ) => {
        const c = u * 2 - 1;
        const near = face === 0 ? c > 0 : c < 0;
        const lift = Math.pow( Math.abs( c ), 4 ) * ( near ? 0.3 : 1 );
        return onFace( face, sr * ( 1 + 0.05 * Math.pow( Math.abs( c ), 6 ) ), u, eave - up * lift );
      };
      const ridgeAt = ( face: 0 | 1, u: number ) => onFace( face, sTop, u, roofTop );
      const N = 24;
      // above the eye, the underside: red rafters running out from the walls to the eave
      {
        for ( const face of [ 0, 1 ] as ( 0 | 1 )[] ) {
          f.fillStyle = this.rgba( redDark, 1 );
          f.beginPath();
          for ( let q = 0; q <= N; q++ ) { f.lineTo( ...eaveAt( face, q / N ) ); }
          f.lineTo( ...onFace( face, sb, 1, ry ) );
          f.lineTo( ...onFace( face, sb, 0, ry ) );
          f.closePath();
          f.fill();
          // the rafters, kept inside the underside so none runs out past its edges
          f.save();
          f.clip();
          f.strokeStyle = this.rgba( face === 0 ? redShade : red, 1 );
          f.lineWidth = line;
          for ( let q = 0; q <= 16; q++ ) {
            const u = q / 16;
            f.beginPath();
            f.moveTo( ...onFace( face, sb, u, ry ) );
            f.lineTo( ...eaveAt( face, u ) );
            f.stroke();
          }
          f.restore();
        }
      }
      // the tiled top, both slopes we see, the left one in shade
      for ( const face of [ 0, 1 ] as ( 0 | 1 )[] ) {
        const tg = f.createLinearGradient( 0, roofTop, 0, eave );
        const base0 = face === 0 ? tileShade : tile;
        tg.addColorStop( 0, this.rgba( tileDark, 1 ) );
        tg.addColorStop( 1, this.rgba( this.mix( base0, [ 255, 255, 255 ], 0.1 ), 1 ) );
        f.fillStyle = tg;
        f.beginPath();
        for ( let q = 0; q <= N; q++ ) { f.lineTo( ...eaveAt( face, q / N ) ); }
        f.lineTo( ...ridgeAt( face, 1 ) );
        f.lineTo( ...ridgeAt( face, 0 ) );
        f.closePath();
        f.fill();
        // the tiles' soft rows running down the slope
        f.strokeStyle = this.rgba( tileDark, 0.22 );
        f.lineWidth = line * 0.9;
        const rows = face === 0 ? 10 : 16;
        for ( let q = 1; q < rows; q++ ) {
          const u = q / rows;
          f.beginPath();
          f.moveTo( ...ridgeAt( face, u ) );
          f.lineTo( ...eaveAt( face, u ) );
          f.stroke();
        }
        // the orange fascia under the eave's lip, and its light
        f.strokeStyle = this.rgba( face === 0 ? this.mix( orange, red, 0.4 ) : orange, 1 );
        f.lineWidth = Math.max( 1.2, step * 0.028 );
        f.beginPath();
        for ( let q = 0; q <= N; q++ ) { const [ px, py ] = eaveAt( face, q / N ); f.lineTo( px, py + line ); }
        f.stroke();
        f.strokeStyle = this.rgba( this.mix( tile, [ 255, 255, 255 ], 0.3 ), 1 );
        f.lineWidth = Math.max( 1, step * 0.016 );
        f.beginPath();
        for ( let q = 0; q <= N; q++ ) { f.lineTo( ...eaveAt( face, q / N ) ); }
        f.stroke();
      }
      // the hip ridges from the corners up to the storey above
      f.strokeStyle = this.rgba( tileDark, 1 );
      f.lineWidth = line * 2;
      f.lineCap = 'round';
      for ( const [ face, u ] of [ [ 0, 0 ], [ 1, 0 ], [ 1, 1 ] ] as [ 0 | 1, number ][] ) {
        f.beginPath();
        f.moveTo( ...eaveAt( face, u ) );
        f.lineTo( ...ridgeAt( face, u ) );
        f.stroke();
      }
    }
    // the spire: its base box, the dark bronze mast with its rings, the finial

  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Fuji's own: peach, coral, mauve, slate, teal. */
  protected five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 245, 217, 208 ], [ 221, 116, 122 ], [ 191, 138, 179 ], [ 95, 125, 159 ], [ 57, 161, 165 ] ];
  }
}
